import { createFileRoute } from "@tanstack/react-router";

import { AlertWarning } from "@kuzco/web/ui";

import type { IntegrationStepConfig } from "../../../components/integrations/IntegrationPageShell";
import { ApiKeyPreviewField } from "../../../components/integrations/ApiKeyPreviewField";
import { CopyBlock } from "../../../components/integrations/CopyBlock";
import { CopyField } from "../../../components/integrations/CopyField";
import { IntegrationPageShell } from "../../../components/integrations/IntegrationPageShell";
import { useNubankCredentials } from "../../../components/integrations/useNubankCredentials";

export const Route = createFileRoute("/_authed/integrations/opencode")({
  component: OpencodeIntegrationPage,
  head: () => ({
    meta: [{ title: "opencode | NBDC" }],
  }),
});

const PROVIDER_ID = "inference-net";

function OpencodeIntegrationPage() {
  const { keyPreview, baseUrl, customModelId, isPending, errorMessage } =
    useNubankCredentials();
  const openAiBaseUrl = `${baseUrl}/v1`;
  const modelKey = customModelId ?? "<custom-model>";

  const opencodeConfig = JSON.stringify(
    {
      $schema: "https://opencode.ai/config.json",
      provider: {
        [PROVIDER_ID]: {
          npm: "@ai-sdk/openai-compatible",
          name: "Inference.net",
          options: {
            baseURL: openAiBaseUrl,
          },
          models: {
            [modelKey]: {
              name: modelKey,
            },
          },
        },
      },
    },
    null,
    2,
  );

  const steps: IntegrationStepConfig[] = [
    {
      title: "Run /connect and pick Other",
      description:
        'In the opencode CLI, run /connect, scroll down in the provider list, and select "Other".',
      content: (
        <CopyBlock
          language="bash"
          value={`$ /connect

┌  Add credential
│
◆  Select provider
│  ...
│  ● Other
└`}
        />
      ),
    },
    {
      title: "Enter a Provider ID",
      description:
        "Pick a memorable ID — you'll reference it in opencode.json below.",
      content: (
        <div className="flex flex-col gap-3">
          <CopyField label="Provider ID" value={PROVIDER_ID} />
          <CopyBlock
            language="bash"
            value={`$ /connect

┌  Add credential
│
◇  Enter provider id
│  ${PROVIDER_ID}
└`}
          />
        </div>
      ),
    },
    {
      title: "Paste your API Key",
      description:
        "opencode will prompt for an API key. Paste the key you saved when you created or reset it.",
      content: (
        <ApiKeyPreviewField
          value={keyPreview}
          isLoading={isPending}
          errorMessage={errorMessage}
        />
      ),
    },
    {
      title: "Add the Provider to opencode.json",
      description:
        "Create or update opencode.json in your project directory with the snippet below.",
      content: (
        <div className="flex flex-col gap-4">
          <CopyField label="Base URL" value={openAiBaseUrl} />
          <CopyField
            label="Custom Model"
            value={customModelId}
            isLoading={isPending}
            errorMessage={errorMessage}
          />
          <CopyBlock
            language="json"
            value={opencodeConfig}
            isLoading={isPending}
            errorMessage={errorMessage}
          />
        </div>
      ),
    },
    {
      title: "Pick the Model",
      description:
        "Run /models inside opencode and your custom provider and model will appear in the list.",
      content: <CopyBlock language="bash" value="/models" />,
    },
  ];

  return (
    <IntegrationPageShell
      title="opencode"
      badge="alpha"
      description="Use the custom Nubank model from the opencode CLI."
      intro={
        <AlertWarning
          className="bg-detail-warning/10"
          title="Alpha integration"
          content="This integration isn't as well tested as the others."
        />
      }
      steps={steps}
    />
  );
}
